import type { MantineTheme } from '@mantine/core';
import { Stack, Textarea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Text } from '@repo/foundations';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { validateCIDRList } from './cidrUtils';

interface CidrWhitelistInputProps {
	theme: MantineTheme;
	defaultValue?: string;
	onChange?: (value: string) => void;
	onValidationError?: (error: string) => void;
}

export interface CidrWhitelistInputRef {
	validate: () => boolean;
	getValue: () => string;
}

const CidrWhitelistInput = forwardRef<
	CidrWhitelistInputRef,
	CidrWhitelistInputProps
>(({ theme, defaultValue = '', onChange, onValidationError }, ref) => {
	const [cidrWhitelist, setCidrWhitelist] = useState(defaultValue);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			const { value } = event.currentTarget;
			setCidrWhitelist(value);
			onChange?.(value);
		},
		[onChange]
	);

	const validateInput = useCallback(() => {
		if (cidrWhitelist.trim()) {
			const validationError = validateCIDRList(cidrWhitelist);
			if (validationError) {
				const errorMessage = validationError;
				showNotification({
					title: 'Invalid CIDR format',
					color: 'red',
					message: errorMessage,
				});
				onValidationError?.(errorMessage);
				return false;
			}
		}
		return true;
	}, [cidrWhitelist, onValidationError]);

	const getValue = useCallback(() => cidrWhitelist, [cidrWhitelist]);

	useImperativeHandle(
		ref,
		() => ({
			validate: validateInput,
			getValue,
		}),
		[validateInput, getValue]
	);

	return (
		<Stack spacing={theme.spacing.xs}>
			<Text size="sm" weight="semibold">
				CIDR Whitelist (Optional)
			</Text>
			<Text size="xs" color="text/secondary/default">
				Enter the external CIDR ranges (e.g., 100.100.100.100/32, 84.12.0.0/16)
				of your running SSH agent.
			</Text>
			<Textarea
				placeholder="0.0.0.0/0"
				minRows={3}
				value={cidrWhitelist}
				onChange={handleChange}
			/>
		</Stack>
	);
});

CidrWhitelistInput.displayName = 'CidrWhitelistInput';

export default CidrWhitelistInput;
